import type { PromptVariable } from '@atlas/types'
import {
	AiProvider,
	BUSINESS_ORIGIN_ENTITY_TYPES,
	PromptAssertionType,
	PromptLifecycle,
	PromptTestCaseOrigin,
	PromptType,
	PromptVariableType,
	selectableModels,
} from '@atlas/types'
import { validateJsonSchema } from '@atlas/core-utils/validations'
import { z } from 'zod'

// ─── Public-facing schema (create / update inputs) ────────────────────────────

/**
 * A prompt's programmatic handle. Lowercase kebab so it reads the same in a URL,
 * an enum member and a log line.
 */
const promptCodeSchema = z
	.string()
	.min(1)
	.max(128)
	.regex(/^[a-z][a-z0-9-]*$/, 'code must be lowercase kebab-case, starting with a letter')

/**
 * A variable key, matching `{{key}}` in the body.
 *
 * Case-SENSITIVE, and deliberately not restricted to snake_case. Accepting only lowercase
 * here would have made the widened placeholder regex pointless: an author could write
 * `{{brandName}}` in the body and then be unable to declare the variable that fills it, which
 * is the same dead end reached from the other side. Renders exactly as authored, so a key and
 * its placeholder can never differ by case.
 */
const variableKeySchema = z
	.string()
	.min(1)
	.max(64)
	.regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'variable key must start with a letter or underscore, then letters/digits/_')

/**
 * One field of an `each` item, matching `{{field}}` inside an `itemTemplate`.
 *
 * Dots are allowed because the renderer resolves a dotted path against the item
 * (`readPath`), so `rule.severity` is a legal reference and must be declarable.
 * Mirrors the renderer's `ITEM_FIELD` pattern — the two must accept the same
 * strings or a field could be declared and then not match, or vice versa.
 */
const itemFieldSchema = z
	.string()
	.min(1)
	.max(128)
	.regex(/^[A-Za-z_][A-Za-z0-9_.]*$/, 'item field must start with a letter or underscore, then letters/digits/_/.')

/**
 * Which type each optional field belongs to.
 *
 * Every cross-field rule on a variable is one of two questions — "does this type
 * have the field it needs?" and "does this field belong on this type?" — so both
 * are read off one table instead of a pair of `if` blocks each. `required: true`
 * means the type cannot resolve a value without it; the others are optional
 * refinements that simply must not appear elsewhere, because a field the render
 * engine never consults for this type is a silent no-op rather than an error.
 */
const TYPE_ONLY_FIELDS = [
	{ field: 'options', type: PromptVariableType.ENUM, required: true },
	{ field: 'listCode', type: PromptVariableType.LIST, required: true },
	{ field: 'itemTemplate', type: PromptVariableType.EACH, required: true },
	{ field: 'itemFields', type: PromptVariableType.EACH, required: false },
	{ field: 'joins', type: PromptVariableType.JOIN, required: true },
] as const satisfies readonly { field: keyof PromptVariable; type: PromptVariableType; required: boolean }[]

/** An empty `options` array is as unresolvable as an absent one. */
const isAbsent = (value: unknown): boolean => value === undefined || (Array.isArray(value) && value.length === 0)

/** `an each variable`, not `a each variable` — the messages are user-facing. */
const article = (word: string): string => (/^[aeiou]/.test(word) ? 'an' : 'a')

/** The `{{field}}` references inside an `itemTemplate`, deduplicated. */
const fieldsReferencedBy = (itemTemplate: string): string[] => [
	...new Set(
		[...itemTemplate.matchAll(/\{\{\s*([A-Za-z_][A-Za-z0-9_.]*)\s*\}\}/g)]
			.map((m) => m[1])
			.filter((field): field is string => field !== undefined),
	),
]

/**
 * `itemTemplate` may only reference fields `itemFields` declares.
 *
 * The same check the body already gets against `variables`, one level down: a
 * template field nothing promised fails per-item at RENDER time, in production,
 * after the tokens are spent. Skipped when `itemFields` is absent, which means
 * "unchecked" rather than "no fields" — a variable authored before the contract
 * existed declares none.
 */
function checkItemContract(v: PromptVariable, ctx: z.RefinementCtx): void {
	if (v.type !== PromptVariableType.EACH || v.itemFields === undefined || v.itemTemplate === undefined) return

	const declared = new Set(v.itemFields)
	const undeclared = fieldsReferencedBy(v.itemTemplate).filter((field) => !declared.has(field))

	if (undeclared.length === 0) return

	ctx.addIssue({
		code: 'custom',
		path: ['itemTemplate'],
		message: `itemTemplate references field(s) not in itemFields: ${undeclared.sort().join(', ')}`,
	})
}

/**
 * Variable definitions are the only guard against a body that cannot render:
 * Postgres accepts any JSON in the column, so a shape rejected here is a shape
 * the render engine would fail on later.
 */
const promptVariableBaseSchema = z.object({
	key: variableKeySchema,
	label: z.string().min(1),
	type: z.enum(PromptVariableType),
	required: z.boolean().default(false),
	default: z.unknown().optional(),
	options: z.array(z.string()).optional(),
	listCode: promptCodeSchema.optional(),
	multiple: z.boolean().optional(),
	separator: z.string().optional(),
	itemTemplate: z.string().min(1).optional(),
	itemFields: z.array(itemFieldSchema).optional(),
	joins: z.array(variableKeySchema).min(1).optional(),
	itemSeparator: z.string().optional(),
	emptyText: z.string().optional(),
	description: z.string().optional(),
})

export const promptVariableSchema = promptVariableBaseSchema.superRefine((v, ctx) => {
	for (const { field, type, required } of TYPE_ONLY_FIELDS) {
		const present = !isAbsent(v[field])

		if (v.type === type && required && !present) {
			ctx.addIssue({ code: 'custom', path: [field], message: `${article(type)} ${type} variable requires ${field}` })
		}
		if (v.type !== type && v[field] !== undefined) {
			ctx.addIssue({
				code: 'custom',
				path: [field],
				message: `${field} is only valid on ${article(type)} ${type} variable`,
			})
		}
	}

	checkItemContract(v, ctx)
})

/**
 * The variable ARRAY, with the rules that need to see its siblings.
 *
 * A `join` names other variables, so it cannot be validated from the item alone —
 * `promptVariableSchema` checks the shape of one variable, this checks the set.
 *
 * A join MAY name another join: the real case needs it. A subject and its free-text
 * direction join with a space to read as one phrase, and that phrase then joins
 * with the scene qualifiers by comma — two separators in one line, which a single
 * flat join cannot express. What is refused is a CYCLE, since that is the only
 * arrangement the renderer cannot resolve.
 */
const promptVariablesSchema = z.array(promptVariableSchema).superRefine((variables, ctx) => {
	const joinsByKey = new Map(
		variables.filter((v) => v.type === PromptVariableType.JOIN).map((v) => [v.key, v.joins ?? []]),
	)
	const declared = new Set(variables.map((v) => v.key))

	variables.forEach((variable, index) => {
		if (variable.type !== PromptVariableType.JOIN) return

		const fail = (message: string) =>
			ctx.addIssue({ code: 'custom', path: [index, 'joins'], message: `join '${variable.key}' ${message}` })

		for (const key of variable.joins ?? []) {
			if (!declared.has(key)) fail(`names '${key}', which no variable declares`)
		}

		// Depth-first from this join; a key already on the current path is a cycle,
		// which would otherwise leave the renderer's second pass with a value that
		// depends on itself.
		const walk = (key: string, path: string[]): void => {
			if (path.includes(key)) {
				fail(`is part of a cycle: ${[...path, key].join(' → ')}`)
				return
			}
			for (const next of joinsByKey.get(key) ?? []) walk(next, [...path, key])
		}
		walk(variable.key, [])
	})
})

/**
 * Model-config OVERRIDES. Every field is optional because the tenant default for
 * the prompt's type supplies the rest; the merged result is what gets frozen at
 * publish.
 */
export const promptModelConfigSchema = z.object({
	// Refused at SAVE rather than at execution. These were free strings, so
	// `"anthropc"` or a model nobody had qualified saved cleanly, froze into a
	// published version, and only failed at the provider mid-run — or worse, was
	// silently substituted, leaving the version naming a model that never executed.
	provider: z.enum(AiProvider).optional(),
	model: z
		.string()
		.min(1)
		.refine((id) => selectableModels().some((m) => m.id === id), {
			message: 'model must be one ATLAS is qualified to call',
		})
		.optional(),
	temperature: z.number().min(0).max(2).optional(),
	maxTokens: z.number().int().positive().optional(),
	messageRole: z.string().min(1).optional(),
	negativePrompt: z.string().optional(),
})

/**
 * The output schema, when a prompt asks for JSON.
 *
 * Absent or null means TEXT — that is the mode, not a missing value, which is why
 * there is no separate `outputMode` field to contradict it. An EMPTY object is
 * refused: it reads as "JSON mode" while telling the provider nothing, so the model
 * gets a structured-output instruction with no structure in it.
 *
 * `nullable` because clearing the schema is how an author switches back to text; the
 * entity writes null rather than dropping the key.
 */
const promptOutputSchemaField = z
	.record(z.string(), z.unknown())
	.refine((schema) => Object.keys(schema).length > 0, {
		message: 'An output schema must describe something — clear it to null for plain text output',
	})
	// Valid JSON is not valid JSON Schema: `{"type": "objct"}` parses cleanly and then
	// fails at the provider, mid-execution, after the tokens are spent.
	.superRefine((schema, ctx) => {
		for (const problem of validateJsonSchema(schema)) {
			ctx.addIssue({ code: 'custom', message: `${problem.path}: ${problem.message}` })
		}
	})
	.nullable()
	.optional()

export const promptCreateSchema = z.object({
	code: promptCodeSchema,
	name: z.string().min(1),
	description: z.string().optional(),
	type: z.enum(PromptType),
	body: z.string().min(1),
	variables: promptVariablesSchema.default([]),
	modelConfig: promptModelConfigSchema.optional(),
	outputSchema: promptOutputSchemaField,
	isActive: z.boolean().default(true),
})

/**
 * `code` is omitted because it is immutable after creation — it must not be
 * expressible in an update at all rather than being rejected later.
 *
 * Declared field-by-field rather than as `promptCreateSchema.partial()`:
 * `.partial()` does NOT strip a field's `.default()`, so the create schema's
 * `variables: [].default()` and `isActive: true.default()` would be injected into
 * every patch. A request changing only `name` would then arrive carrying
 * `variables: []` and silently wipe the prompt's variable schema.
 */
export const promptUpdateSchema = z.object({
	name: z.string().min(1).optional(),
	description: z.string().optional(),
	type: z.enum(PromptType).optional(),
	body: z.string().min(1).optional(),
	variables: promptVariablesSchema.optional(),
	modelConfig: promptModelConfigSchema.optional(),
	outputSchema: promptOutputSchemaField,
	isActive: z.boolean().optional(),
	lifecycle: z.enum(PromptLifecycle).optional(),
})

// ─── Lists ────────────────────────────────────────────────────────────────────

export const promptVariableListItemSchema = z.object({
	value: z.string().min(1),
	label: z.string().min(1),
})

export const promptVariableListCreateSchema = z.object({
	code: promptCodeSchema,
	name: z.string().min(1),
	description: z.string().optional(),
	items: z.array(promptVariableListItemSchema).default([]),
	isActive: z.boolean().default(true),
})

/** Field-by-field for the same reason as `promptUpdateSchema` — `.partial()` keeps `.default()`. */
export const promptVariableListUpdateSchema = z.object({
	name: z.string().min(1).optional(),
	description: z.string().optional(),
	items: z.array(promptVariableListItemSchema).optional(),
	isActive: z.boolean().optional(),
})

// ─── Business origin ─────────────────────────────────────────────────────────

/**
 * The business entity an execution is performed FOR — a work order, campaign or
 * message (ADR-0034). Distinct from the entity an audit event is ABOUT, which is
 * always the prompt.
 *
 * Constrained to `BUSINESS_ORIGIN_ENTITY_TYPES` rather than the whole
 * `EventEntityType` enum; that constant documents why the set is a subset and is
 * the single place to widen it.
 */
export const businessOriginSchema = z.object({
	type: z.enum(BUSINESS_ORIGIN_ENTITY_TYPES),
	id: z.string().min(1),
})

export type BusinessOriginInput = z.infer<typeof businessOriginSchema>

/**
 * Tenant-wide settings patch. Every field optional so a caller can flip one setting
 * without restating the others; the service merges over what is stored.
 */
export const promptTenantSettingsSchema = z.object({
	requirePassingEvals: z.boolean().optional(),
})

// ─── Render ───────────────────────────────────────────────────────────────────

export const promptRenderSchema = z.object({
	values: z.record(z.string(), z.unknown()).default({}),
})

/**
 * Body for the async trigger endpoints (eval run, test-case and schema drafting).
 *
 * `origin` is optional because an author's manual run genuinely has no business
 * origin — the spec requires omitting it rather than supplying a placeholder, so
 * there is no default here and none should be added.
 */
export const executionOriginSchema = z.object({
	origin: businessOriginSchema.optional(),
})

export type ExecutionOriginInput = z.infer<typeof executionOriginSchema>

export type PromptCreateInput = z.infer<typeof promptCreateSchema>
export type PromptUpdateInput = z.infer<typeof promptUpdateSchema>
export type PromptVariableListCreateInput = z.infer<typeof promptVariableListCreateSchema>
export type PromptVariableListUpdateInput = z.infer<typeof promptVariableListUpdateSchema>
export type PromptRenderInput = z.infer<typeof promptRenderSchema>

// ─── Eval test cases ──────────────────────────────────────────────────────────

/**
 * One assertion on a test case.
 *
 * Validated as a discriminated union so a `contains` without a value, or a
 * `max_length` without a max, is refused at the boundary rather than evaluating to
 * a confusing failure later.
 */
export const promptAssertionSchema = z.discriminatedUnion('type', [
	z.object({ type: z.literal(PromptAssertionType.CONTAINS), value: z.string().min(1) }),
	z.object({ type: z.literal(PromptAssertionType.NOT_CONTAINS), value: z.string().min(1) }),
	z.object({ type: z.literal(PromptAssertionType.MAX_LENGTH), max: z.number().int().positive() }),
	z.object({ type: z.literal(PromptAssertionType.MATCHES_OUTPUT_SCHEMA) }),
	z.object({ type: z.literal(PromptAssertionType.LLM_RUBRIC), value: z.string().min(1) }),
])

export const promptTestCaseCreateSchema = z.object({
	name: z.string().min(1),
	values: z.record(z.string(), z.unknown()).default({}),
	assertions: z.array(promptAssertionSchema).default([]),
	origin: z.enum(PromptTestCaseOrigin).optional(),
})

/** Field-by-field for the same reason as `promptUpdateSchema` — `.partial()` keeps `.default()`. */
export const promptTestCaseUpdateSchema = z.object({
	name: z.string().min(1).optional(),
	values: z.record(z.string(), z.unknown()).optional(),
	assertions: z.array(promptAssertionSchema).optional(),
})

export type PromptAssertionInput = z.infer<typeof promptAssertionSchema>
export type PromptTestCaseInput = z.infer<typeof promptTestCaseCreateSchema>
export type PromptTestCaseUpdateInput = z.infer<typeof promptTestCaseUpdateSchema>
